import type { BlogPostSEOProps } from './types'
import {
  generateBlogPostingJsonLd,
  generateFAQJsonLd,
  generateHowToJsonLd,
  generateBreadcrumbJsonLd,
} from './generators'

/**
 * BlogPostSEO - Renders JSON-LD structured data for blog posts
 *
 * Outputs script tags with JSON-LD for:
 * - BlogPosting schema (always)
 * - BreadcrumbList schema (when includeBreadcrumbs is true, default)
 * - FAQPage schema (when faq items are provided)
 * - HowTo schema (when howTo data is provided)
 *
 * @example
 * ```tsx
 * <BlogPostSEO
 *   post={{
 *     slug: 'getting-started',
 *     title: 'Getting Started Guide',
 *     description: 'Learn how to get started with our product',
 *     date: '2024-01-15',
 *     author: { name: 'John Doe', url: '/authors/john' },
 *     tags: ['tutorial', 'beginner'],
 *   }}
 *   config={{
 *     baseUrl: 'https://example.com',
 *     siteName: 'Example Blog',
 *     publisher: { name: 'Example Inc', logo: '/logo.png' },
 *   }}
 *   faq={[
 *     { question: 'What is this?', answer: 'A getting started guide.' },
 *   ]}
 * />
 * ```
 */
export function BlogPostSEO({
  post,
  config,
  faq,
  howTo,
  includeBreadcrumbs = true,
}: BlogPostSEOProps) {
  const blogPostingSchema = generateBlogPostingJsonLd(post, config)
  const breadcrumbSchema = includeBreadcrumbs
    ? generateBreadcrumbJsonLd(post, config)
    : null
  const faqSchema = faq && faq.length > 0 ? generateFAQJsonLd(faq) : null
  const howToSchema = howTo ? generateHowToJsonLd(howTo, config.baseUrl) : null

  return (
    <>
      <script
        type="application/ld+json"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD requires innerHTML
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(blogPostingSchema),
        }}
      />
      {breadcrumbSchema && (
        <script
          type="application/ld+json"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD requires innerHTML
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbSchema),
          }}
        />
      )}
      {faqSchema && (
        <script
          type="application/ld+json"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD requires innerHTML
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      )}
      {howToSchema && (
        <script
          type="application/ld+json"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: JSON-LD requires innerHTML
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(howToSchema),
          }}
        />
      )}
    </>
  )
}
